import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import API_BASE_URL from "../../../config/api";
import { FaChevronDown, FaChevronUp, FaFileDownload, FaUsers, FaUserTie, FaCheck, FaCalendarAlt, FaLayerGroup, FaExternalLinkAlt, FaSearch, FaFilter } from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";

function PCReportContainer() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedProjects, setExpandedProjects] = useState({});

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedThrustArea, setSelectedThrustArea] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 800 });
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/projects/department/${user?.department}`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });
        const data = await response.data;
        if (response.status === 200) {
          setProjects(data.data);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Filtered projects logic
  const filteredProjects = projects.filter(project => {
    const matchesSearch =
      (project.projectTitle?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (project.teamMembers?.some(member => member.toLowerCase().includes(searchTerm.toLowerCase())));

    const matchesThrustArea = selectedThrustArea
      ? project.thrustArea === selectedThrustArea
      : true;

    const matchesStatus = selectedStatus
      ? project.status === selectedStatus
      : true;

    return matchesSearch && matchesThrustArea && matchesStatus;
  });

  // Get unique thrust areas for filter dropdown
  const uniqueThrustAreas = [...new Set(projects.map(p => p.thrustArea).filter(Boolean))];

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Helper function to safely extract text from a preference object or string
  const getPreferenceText = (preference) => {
    if (!preference) return '';
    if (typeof preference === 'object') {
      return preference.email || (preference.guideId ? `Guide ID: ${preference.guideId}` : 'Unnamed Guide');
    }
    return preference;
  };

  const generateExcel = () => {
    // 1. Prepare Data with Header Row
    const wsData = [
      [`Department of ${user?.department || "Engineering"}`], // Row 0: Merged Header
      [                                                       // Row 1: Column Headers
        "Project Title",
        "Team Members",
        "Assigned Guide",
        "Assigned Co-Guide",
        "Thrust Area",
        "Status",
      ],
    ];

    projects.forEach((project) => {
      // Format Team Members
      const teamMembers = project.teamMembers ? project.teamMembers.join(", ") : "";

      // Get Assigned Guide Name
      const assignedGuide = project.guide?.assigned?.email || "Not Assigned";

      // Get Assigned Co-Guide Name
      const assignedCoGuide = project.coGuides?.assigned?.email || "Not Assigned";

      const row = [
        project.projectTitle,
        teamMembers,
        assignedGuide,
        assignedCoGuide,
        project.thrustArea || "Not Assigned",
        project.status || "Pending",
      ];

      wsData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projects");

    // 2. Adjust column widths
    ws["!cols"] = [
      { wch: 30 }, // Title
      { wch: 40 }, // Team Members
      { wch: 25 }, // Guide
      { wch: 25 }, // Co-Guide
      { wch: 40 }, // Thrust Area
      { wch: 15 }, // Status
    ];

    // 3. Merge Header Cells (A1 to E1)
    if (!ws["!merges"]) ws["!merges"] = [];
    ws["!merges"].push({ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } });

    const range = XLSX.utils.decode_range(ws["!ref"]);

    // Style Header Row (Row 0)
    const headerCell = ws[XLSX.utils.encode_cell({ r: 0, c: 0 })];
    if (headerCell) {
      headerCell.s = {
        font: { name: "Arial", sz: 14, bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "B7202E" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
        }
      };
    }

    // Style Column Headers (Row 1) and Data Rows
    for (let R = 1; R <= range.e.r; ++R) {
      for (let C = 0; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;

        // Base border style
        const cellStyle = {
          border: {
            top: { style: "thin", color: { rgb: "CCCCCC" } },
            bottom: { style: "thin", color: { rgb: "CCCCCC" } },
            left: { style: "thin", color: { rgb: "CCCCCC" } },
            right: { style: "thin", color: { rgb: "CCCCCC" } },
          },
          alignment: { vertical: "top", wrapText: true }
        };

        // Header specific modifications (Row 1)
        if (R === 1) {
          cellStyle.font = { bold: true, color: { rgb: "333333" } };
          cellStyle.fill = { fgColor: { rgb: "F3F4F6" } }; // Light gray
        }

        ws[cellAddress].s = cellStyle;
      }
    }

    XLSX.writeFile(wb, `projects_report_${new Date().toLocaleDateString("en-IN", { year: "numeric", month: "2-digit", day: "2-digit" })}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-[#B7202E] rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 font-medium">Loading projects...</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'bg-green-100 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      case 'modifications': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'guide-assigned': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-8 font-sans">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4" data-aos="fade-down">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Project Reports</h1>
            <p className="mt-1 text-gray-500">Overview of all submitted projects</p>
          </div>
          <button
            className="bg-[#B7202E] text-white px-6 py-3 rounded-xl font-bold shadow-sm hover:bg-[#961925] hover:shadow-lg hover:-translate-y-0.5 transition-all flex items-center gap-2 w-full md:w-auto justify-center"
            onClick={generateExcel}
          >
            <FaFileDownload className="text-lg" /> Download Report
          </button>
        </div>

        {/* Search and Filters */}
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row gap-4 justify-between items-center">

          {/* Search Bar */}
          <div className="relative w-full md:w-1/3">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Title or Student..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto">

            {/* Thrust Area Filter */}
            <div className="relative w-full md:w-64">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <FaFilter size={12} />
              </div>
              <select
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-all appearance-none cursor-pointer bg-white text-gray-700 text-sm"
                value={selectedThrustArea}
                onChange={(e) => setSelectedThrustArea(e.target.value)}
              >
                <option value="">All Thrust Areas</option>
                {uniqueThrustAreas.map((area, index) => (
                  <option key={index} value={area}>{area}</option>
                ))}
              </select>
              <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={12} />
            </div>

            {/* Status Filter */}
            <div className="relative w-full md:w-48">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <FaFilter size={12} />
              </div>
              <select
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-all appearance-none cursor-pointer bg-white text-gray-700 text-sm"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Modifications">Modifications</option>
                <option value="Pending">Pending</option>
                <option value="Guide-Assigned">Guide Assigned</option>
              </select>
              <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={12} />
            </div>

          </div>
        </div>

        <div className="space-y-4">
          {filteredProjects.length > 0 ? (
            filteredProjects.map((project, index) => (
              <div
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md"
                key={project._id || project.id}
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >
                <div
                  className="p-4 md:p-5 flex flex-row justify-between items-center cursor-pointer bg-gray-50/30 hover:bg-gray-50 transition-colors gap-4"
                  onClick={() => toggleProject(project._id)}
                >
                  <div className="flex-1 gap-2">
                    <button onClick={() => navigate(`/project/${project._id}`)}>
                      <h3 className="text-lg font-bold text-gray-800 mb-1">{project.projectTitle} <FaExternalLinkAlt className="inline p-0 h-3" /></h3>
                    </button>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border ${getStatusColor(project.status)}`}>
                        {project.status || "Pending"}
                      </span>
                      {project.thrustArea && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          {project.thrustArea}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-gray-400">
                    {expandedProjects[project._id || project.id] ? <FaChevronUp /> : <FaChevronDown />}
                  </div>
                </div>

                <div
                  className={`transition-all duration-300 ease-in-out border-t border-gray-100 ${expandedProjects[project._id || project.id] ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
                    }`}
                >
                  <div className="p-5 md:p-6 grid gap-6 md:grid-cols-2">

                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h4 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                        <FaUsers /> Team Members
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {project.teamMembers && project.teamMembers.length > 0 ? (
                          project.teamMembers.map((member, idx) => (
                            <span key={idx} className="bg-white px-3 py-1.5 rounded-md border border-gray-200 text-sm font-medium text-gray-700 shadow-sm">
                              {member}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm italic">No team members listed</span>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h4 className="text-xs font-bold text-[#B7202E] uppercase tracking-wider mb-3 flex items-center gap-2">
                        <FaUserTie /> Project Guides
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Assigned Guide</p>
                          {project.guide?.assigned?.email ? (
                            <div className="flex items-center gap-2 text-gray-800 font-medium bg-white px-3 py-2 rounded border border-gray-200 shadow-sm">
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              {project.guide.assigned.email}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-gray-400 italic bg-gray-100/50 px-3 py-2 rounded border border-gray-200 border-dashed">
                              <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                              Not Assigned
                            </div>
                          )}
                        </div>

                        <div>
                          <p className="text-xs text-gray-500 font-semibold uppercase mb-1">Assigned Co-Guide</p>
                          {project.coGuides?.assigned?.email ? (
                            <div className="flex items-center gap-2 text-gray-800 font-medium bg-white px-3 py-2 rounded border border-gray-200 shadow-sm">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              {project.coGuides.assigned.email}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-gray-400 italic bg-gray-100/50 px-3 py-2 rounded border border-gray-200 border-dashed">
                              <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                              Not Assigned
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
              <FaLayerGroup className="mx-auto text-4xl text-gray-300 mb-4" />
              <h3 className="text-lg font-bold text-gray-800 mb-1">No Projects Found</h3>
              <p className="text-gray-500">There are no projects available to display at the moment.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PCReportContainer;
